/**
 * Authentication Type Definitions
 * Central location for all auth-related types
 */

// ============== User Types ==============

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  mobile?: string;
  verified?: boolean;
}

export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

// ============== Auth State Types ==============

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  tokenExpiry: number | null;
}

// ============== Request/Response Types ==============

export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiryMillis: number;
}

export interface SendOtpRequest {
  mobile: string;
}

export interface SendOtpResponse {
  message: string;
  otpSentAt: string;
}

export interface VerifyOtpRequest {
  mobile: string;
  otp: string;
}

export interface VerifyOtpResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiryMillis: number;
  isNewUser: boolean;
  requiresRegistration?: boolean;
}

export interface CreateUserRequest {
  name: string;
  email?: string;
}

export interface CreateUserResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiryMillis: number;
  user: User;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  accessExpiryMillis: number;
}

// ============== Auth Hook Return Type ==============

export interface AuthHookReturn {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  tokenExpiry: number | null;
  
  // Methods
  adminLogin: (credentials: AdminLoginRequest) => Promise<{ success: boolean; data?: AdminLoginResponse; error?: unknown }>;
  sendOtp: (data: SendOtpRequest) => Promise<{ success: boolean; data?: SendOtpResponse; error?: unknown }>;
  verifyOtp: (data: VerifyOtpRequest) => Promise<{ success: boolean; data?: VerifyOtpResponse; error?: unknown }>;
  createUser: (data: CreateUserRequest) => Promise<{ success: boolean; data?: CreateUserResponse; error?: unknown }>;
  refreshAccessToken: () => Promise<{ success: boolean; data?: RefreshTokenResponse; error?: unknown }>;
  logout: () => Promise<void>;
  checkAuth: () => void;
  
  // Loading states
  isLoading: boolean;
  isAdminLoginLoading: boolean;
  isSendOtpLoading: boolean;
  isVerifyOtpLoading: boolean;
  isCreateUserLoading: boolean;
  isRefreshLoading: boolean;
  isLogoutLoading: boolean;
}
